/**
 * HistoricalPricesPanel — bouton explicite + bandeau status pour le
 * pré-fetch CoinGecko range API (Sprint V2.E.UI.4).
 *
 * Conformément aux 3 décisions arbitrées :
 *  1. Bouton explicite (pas de pré-fetch automatique) — l'utilisateur
 *     contrôle quand l'appel réseau a lieu.
 *  2. Cache localStorage no-TTL (géré par le service en amont).
 *  3. Warning + fallback graceful — calculator V2.E2 a déjà sa
 *     heuristique 24h en backup.
 *
 * Pure presentation — state lifté au parent (TaxCenter). Le panel
 * affiche :
 *   - bouton "Charger les prix historiques" + nombre de paires à fetch
 *   - bandeau status (loading / success / partial-warning / total-error)
 *   - bouton "Vider le cache" en escape hatch
 */

import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import type { PriceSnapshotsResult } from '../../services/cryptoHistoricalPriceCache';

interface HistoricalPricesPanelProps {
  /** Total (coinId, dateISO) pairs needed by the formula. Drives the
   *  "X paires à charger" hint + disable when 0. */
  requiredPairs: number;
  /** Result of the last fetch — null when never run. */
  result: PriceSnapshotsResult | null;
  /** True while a fetch is in flight. */
  isFetching: boolean;
  /** Trigger a fresh fetch. */
  onFetch: () => void;
  /** Clear the on-device cache. */
  onClearCache: () => void;
}

export function HistoricalPricesPanel({
  requiredPairs,
  result,
  isFetching,
  onFetch,
  onClearCache,
}: HistoricalPricesPanelProps) {
  const { t } = useTranslation();

  // Don't render at all when no event needs a snapshot — keeps the
  // calc tab clean for mono-asset users.
  if (requiredPairs === 0) return null;

  const status = (() => {
    if (isFetching) return 'loading';
    if (!result) return 'idle';
    if (result.totalFailure) return 'error';
    if (result.missed > 0) return 'warning';
    return 'success';
  })();

  const statusBg = {
    idle: 'transparent',
    loading: 'rgba(99, 102, 241, 0.1)',
    success: 'rgba(16, 185, 129, 0.1)',
    warning: 'rgba(245, 158, 11, 0.1)',
    error: 'rgba(239, 68, 68, 0.1)',
  }[status];

  const statusColor = {
    idle: 'var(--muted)',
    loading: '#6366f1',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  }[status];

  const statusIcon = {
    idle: <Icons.LineChart size={16} />,
    loading: <Icons.RefreshCw size={16} />,
    success: <Icons.Check size={16} />,
    warning: <Icons.AlertTriangle size={16} />,
    error: <Icons.AlertCircle size={16} />,
  }[status];

  return (
    <div
      style={{
        padding: '16px',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        background: 'var(--bg-secondary)',
        marginBottom: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '12px',
        }}
      >
        <div style={{ flex: 1, minWidth: '240px' }}>
          <h3
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            {t(
              'taxCenter.historicalPrices.title',
              'Prix historiques (précision Art. 150 VH bis)',
            )}
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0' }}>
            {t(
              'taxCenter.historicalPrices.help',
              'Pour les portefeuilles multi-actifs, charge les prix EUR à la date de chaque cession via CoinGecko. Améliore la précision du calcul vs l\'estimation 24h par défaut.',
            )}{' '}
            <strong>
              {t(
                'taxCenter.historicalPrices.pairsCount',
                '{{count}} paires à charger',
              ).replace('{{count}}', String(requiredPairs))}
            </strong>
            .
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onFetch}
            disabled={isFetching || requiredPairs === 0}
            style={{
              padding: '8px 14px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              background: 'var(--primary)',
              color: 'white',
              cursor:
                isFetching || requiredPairs === 0 ? 'not-allowed' : 'pointer',
              opacity: isFetching || requiredPairs === 0 ? 0.5 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {isFetching ? <Icons.RefreshCw size={12} /> : <Icons.LineChart size={12} />}
            {isFetching
              ? t('taxCenter.historicalPrices.loading', 'Chargement…')
              : t('taxCenter.historicalPrices.fetch', 'Charger les prix')}
          </button>
          {result && (
            <button
              type="button"
              onClick={onClearCache}
              title={t(
                'taxCenter.historicalPrices.clearCacheHelp',
                'Vide le cache local des prix historiques. À utiliser si vous suspectez des données erronées.',
              )}
              style={{
                padding: '8px 10px',
                fontSize: '13px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--muted)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Icons.Trash2 size={12} />
              {t('taxCenter.historicalPrices.clearCache', 'Vider')}
            </button>
          )}
        </div>
      </div>

      {/* Status banner — visible only after a fetch attempt. */}
      {status !== 'idle' && (
        <div
          role={status === 'error' || status === 'warning' ? 'alert' : 'status'}
          style={{
            padding: '10px 12px',
            borderRadius: '6px',
            background: statusBg,
            color: statusColor,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
          }}
        >
          <span style={{ marginTop: '1px' }}>{statusIcon}</span>
          <span>
            {status === 'loading' && t(
              'taxCenter.historicalPrices.statusLoading',
              'Chargement des prix historiques en cours…',
            )}
            {status === 'success' && result && t(
              'taxCenter.historicalPrices.statusSuccess',
              '{{resolved}} paires chargées avec succès. Le calcul utilise des prix EUR exacts.',
            ).replace('{{resolved}}', String(result.resolved))}
            {status === 'warning' && result && (
              <>
                {t(
                  'taxCenter.historicalPrices.statusPartial',
                  '{{resolved}} paires chargées sur {{requested}} demandées. {{missed}} ont échoué — précision réduite sur ces dates. Réessayer dans quelques minutes.',
                )
                  .replace('{{resolved}}', String(result.resolved))
                  .replace('{{requested}}', String(result.requested))
                  .replace('{{missed}}', String(result.missed))}
              </>
            )}
            {status === 'error' && t(
              'taxCenter.historicalPrices.statusFailure',
              'Impossible de joindre CoinGecko. Le calcul utilise l\'estimation 24h par défaut (précision réduite). Réessayer plus tard.',
            )}
          </span>
        </div>
      )}
    </div>
  );
}
